import React, { useState, useEffect } from 'react';
import {
  User,
  Server,
  Shield,
  CreditCard,
  Users,
  Edit,
  Trash2,
  Plus,
  Eye,
  EyeOff,
  Wand2,
  MapPin,
  Mail,
  Phone,
  Lock,
  ArrowLeft,
} from 'lucide-react';
import type {
  Customer,
  Contact,
  Product,
  Device,
  Credential,
  BillingRecord,
} from '../types';
import {
  customersApi,
  contactsApi,
  productsApi,
  devicesApi,
  credentialsApi,
  billingApi,
} from '../lib/supabase';
import { generateCustomerSummary, isAIAvailable } from '../services/geminiService';
import { Card, Button, Modal, Input, Select, StatusBadge, LoadingSpinner } from './UIComponents';

interface Props {
  customerId: string;
  onBack: () => void;
  onUpdate: () => void;
}

type TabType = 'overview' | 'contacts' | 'products' | 'devices' | 'credentials' | 'billing';

export const CustomerDetail: React.FC<Props> = ({ customerId, onBack, onUpdate }) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [billing, setBilling] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<string | null>(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [customerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const customerData = await customersApi.getById(customerId);
      if (customerData) {
        setCustomer(customerData);
        setContacts(customerData.contacts || []);
        setProducts(customerData.products || []);
        setDevices(customerData.devices || []);
        setCredentials(customerData.credentials || []);
        setBilling((customerData.billing_records || []).sort((a, b) => b.month.localeCompare(a.month)));
      }
    } catch (error) {
      console.error('Error loading customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!customer) return;
    setIsGeneratingSummary(true);
    const data = {
      customer: { name: customer.name, address: customer.address, primary_contact_name: customer.primary_contact_name },
      activeProducts: products.filter((p) => p.status === 'Active').map((p) => ({ name: p.name, cost: p.cost, type: p.type })),
      devices: devices.map((d) => ({ name: d.name, type: d.type, status: d.status })),
      mrr: products.filter((p) => p.status === 'Active').reduce((acc, p) => acc + p.cost, 0),
      billingStatus: billing.slice(0, 3).map((b) => ({ month: b.month, status: b.status })),
    };
    const result = await generateCustomerSummary(data);
    setSummary(result);
    setIsGeneratingSummary(false);
  };

  const handleDelete = async (type: string, id: string) => {
    if (!confirm('Are you sure you want to delete this record?')) return;
    try {
      if (type === 'contact') await contactsApi.delete(id);
      else if (type === 'product') await productsApi.delete(id);
      else if (type === 'device') await devicesApi.delete(id);
      else if (type === 'credential') await credentialsApi.delete(id);
      else if (type === 'billing') await billingApi.delete(id);
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const saveItem = async (type: string, data: any) => {
    setSaving(true);
    const isNew = !data.id;
    try {
      if (type === 'contact') {
        if (isNew) await contactsApi.create({ ...data, customer_id: customerId });
        else await contactsApi.update(data.id, data);
      } else if (type === 'product') {
        if (isNew) await productsApi.create({ ...data, customer_id: customerId });
        else await productsApi.update(data.id, data);
      } else if (type === 'device') {
        if (isNew) await devicesApi.create({ ...data, customer_id: customerId });
        else await devicesApi.update(data.id, data);
      } else if (type === 'credential') {
        if (isNew) await credentialsApi.create({ ...data, customer_id: customerId });
        else await credentialsApi.update(data.id, data);
      } else if (type === 'billing') {
        const qty = parseFloat(data.quantity) || 0;
        const price = parseFloat(data.unit_price) || 0;
        const totalEx = qty * price;
        const gst = totalEx * 0.1;
        const billingData = { ...data, gst_amount: gst, total_inc_gst: totalEx + gst };
        if (isNew) await billingApi.create({ ...billingData, customer_id: customerId });
        else await billingApi.update(data.id, billingData);
      } else if (type === 'customer' && customer) {
        await customersApi.update(customer.id, data);
        onUpdate();
      }
      setModalType(null);
      setEditingItem(null);
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const CredentialRow = ({ item }: { item: Credential }) => {
    const [visible, setVisible] = useState(false);
    return (
      <tr className="border-b last:border-0 hover:bg-slate-50 transition-colors">
        <td className="p-3 font-medium text-slate-900">{item.system_name}</td>
        <td className="p-3 text-slate-500 text-sm">
          {item.url && (
            <a href={item.url.startsWith('http') ? item.url : `https://${item.url}`} target="_blank" rel="noreferrer" className="text-accent hover:underline">
              {item.url}
            </a>
          )}
        </td>
        <td className="p-3 text-sm text-slate-600">{item.username}</td>
        <td className="p-3 font-mono text-sm">
          <div className="flex items-center gap-2">
            <span className="bg-slate-100 px-2 py-0.5 rounded text-xs">{visible ? item.password : '••••••••'}</span>
            <button onClick={() => setVisible(!visible)} className="text-slate-400 hover:text-slate-600">
              {visible ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </td>
        <td className="p-3 text-right">
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setModalType('credential'); }}><Edit size={14} /></Button>
            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('credential', item.id)}><Trash2 size={14} /></Button>
          </div>
        </td>
      </tr>
    );
  };

  if (loading || !customer) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>;
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: User },
    { id: 'contacts' as TabType, label: 'Contacts', icon: Users },
    { id: 'products' as TabType, label: 'Products', icon: Shield },
    { id: 'devices' as TabType, label: 'Devices', icon: Server },
    { id: 'credentials' as TabType, label: 'Logins', icon: Lock },
    { id: 'billing' as TabType, label: 'Billing', icon: CreditCard },
  ];

  const mrr = products.filter((p) => p.status === 'Active').reduce((sum, p) => sum + p.cost, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <div className="flex-1">
          <button onClick={onBack} className="text-xs font-semibold text-slate-500 hover:text-accent mb-3 flex items-center gap-1 uppercase tracking-wider transition-colors">
            <ArrowLeft size={14} /> Back to Fleet
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">{customer.name}</h1>
            <StatusBadge status={mrr > 1000 ? 'Key Client' : 'Standard'} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-1 gap-x-6 mt-3 text-sm text-slate-500">
            <div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" /> {customer.address || 'No address'}</div>
            <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400" /> {customer.primary_contact_email || '-'}</div>
            <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> {customer.primary_contact_phone || '-'}</div>
          </div>
        </div>
        <div className="flex gap-2 w-full lg:w-auto">
          {isAIAvailable() && (
            <Button variant="secondary" onClick={handleGenerateSummary} disabled={isGeneratingSummary}>
              <Wand2 size={16} /> {isGeneratingSummary ? 'Analyzing...' : 'AI Summary'}
            </Button>
          )}
          <Button onClick={() => { setEditingItem(customer); setModalType('customer'); }}>
            <Edit size={16} /> Edit Profile
          </Button>
        </div>
      </div>

      {summary && (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
          <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><Wand2 size={16} className="text-accent" /> AI Summary</h3>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{summary}</div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-px">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${activeTab === tab.id ? 'bg-white text-accent border border-slate-200 border-b-white -mb-px' : 'text-slate-500 hover:text-slate-900'}`}>
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <h3 className="font-bold text-slate-900 mb-4">Company Details</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">ABN</dt><dd className="font-medium">{customer.abn || '-'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Primary Contact</dt><dd className="font-medium">{customer.primary_contact_name || '-'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Monthly Revenue</dt><dd className="font-medium text-emerald-600">${mrr.toLocaleString()}</dd></div>
            </dl>
          </Card>
          <Card>
            <h3 className="font-bold text-slate-900 mb-4">Notes</h3>
            <p className="text-sm text-slate-600">{customer.notes || 'No notes.'}</p>
          </Card>
        </div>
      )}

      {activeTab === 'contacts' && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Contacts</h3>
            <Button onClick={() => { setEditingItem(null); setModalType('contact'); }}><Plus size={16} /> Add</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Role</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Phone</th><th className="p-3"></th></tr></thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-medium">{c.name}</td><td className="p-3">{c.role || '-'}</td><td className="p-3">{c.email || '-'}</td><td className="p-3">{c.phone || '-'}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => { setEditingItem(c); setModalType('contact'); }}><Edit size={14} /></Button><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('contact', c.id)}><Trash2 size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {contacts.length === 0 && <div className="p-8 text-center text-slate-400">No contacts.</div>}
        </Card>
      )}

      {activeTab === 'products' && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Products</h3>
            <Button onClick={() => { setEditingItem(null); setModalType('product'); }}><Plus size={16} /> Add</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Type</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Cost/mo</th><th className="p-3"></th></tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-medium">{p.name}</td><td className="p-3">{p.type}</td><td className="p-3"><StatusBadge status={p.status} /></td><td className="p-3 text-right font-medium">${p.cost}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => { setEditingItem(p); setModalType('product'); }}><Edit size={14} /></Button><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('product', p.id)}><Trash2 size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && <div className="p-8 text-center text-slate-400">No products.</div>}
        </Card>
      )}

      {activeTab === 'devices' && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Devices</h3>
            <Button onClick={() => { setEditingItem(null); setModalType('device'); }}><Plus size={16} /> Add</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Type</th><th className="p-3 text-left">OS</th><th className="p-3 text-left">RustDesk</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr></thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-medium">{d.name}</td><td className="p-3">{d.type}</td><td className="p-3">{d.os || '-'}</td><td className="p-3 font-mono text-xs">{d.rustdesk_id || '-'}</td><td className="p-3"><StatusBadge status={d.status} /></td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => { setEditingItem(d); setModalType('device'); }}><Edit size={14} /></Button><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('device', d.id)}><Trash2 size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {devices.length === 0 && <div className="p-8 text-center text-slate-400">No devices.</div>}
        </Card>
      )}

      {activeTab === 'credentials' && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Saved Logins</h3>
            <Button onClick={() => { setEditingItem(null); setModalType('credential'); }}><Plus size={16} /> Add</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3 text-left">System</th><th className="p-3 text-left">URL</th><th className="p-3 text-left">Username</th><th className="p-3 text-left">Password</th><th className="p-3"></th></tr></thead>
            <tbody>{credentials.map((c) => <CredentialRow key={c.id} item={c} />)}</tbody>
          </table>
          {credentials.length === 0 && <div className="p-8 text-center text-slate-400">No credentials.</div>}
        </Card>
      )}

      {activeTab === 'billing' && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Billing</h3>
            <Button onClick={() => { setEditingItem(null); setModalType('billing'); }}><Plus size={16} /> Add</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3 text-left">Month</th><th className="p-3 text-left">Description</th><th className="p-3 text-right">Total</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr></thead>
            <tbody>
              {billing.map((b) => (
                <tr key={b.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-medium">{b.month}</td><td className="p-3">{b.description || '-'}</td><td className="p-3 text-right font-medium">${b.total_inc_gst.toFixed(2)}</td><td className="p-3"><StatusBadge status={b.status} /></td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => { setEditingItem(b); setModalType('billing'); }}><Edit size={14} /></Button><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('billing', b.id)}><Trash2 size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {billing.length === 0 && <div className="p-8 text-center text-slate-400">No billing records.</div>}
        </Card>
      )}

      {/* Modals */}
      <Modal isOpen={modalType === 'customer'} onClose={() => setModalType(null)} title="Edit Customer">
        <GenericForm fields={[{ name: 'name', label: 'Name', required: true }, { name: 'abn', label: 'ABN' }, { name: 'address', label: 'Address' }, { name: 'primary_contact_name', label: 'Contact' }, { name: 'primary_contact_email', label: 'Email' }, { name: 'primary_contact_phone', label: 'Phone' }, { name: 'notes', label: 'Notes', type: 'textarea' }]} initialData={editingItem} onSubmit={(d) => saveItem('customer', d)} saving={saving} />
      </Modal>
      <Modal isOpen={modalType === 'contact'} onClose={() => setModalType(null)} title="Contact">
        <GenericForm fields={[{ name: 'name', label: 'Name', required: true }, { name: 'role', label: 'Role' }, { name: 'email', label: 'Email' }, { name: 'phone', label: 'Phone' }]} initialData={editingItem} onSubmit={(d) => saveItem('contact', d)} saving={saving} />
      </Modal>
      <Modal isOpen={modalType === 'product'} onClose={() => setModalType(null)} title="Product">
        <GenericForm fields={[{ name: 'name', label: 'Name', required: true }, { name: 'type', label: 'Type', type: 'select', options: ['Licence', 'Subscription', 'Hardware', 'Support', 'Infrastructure', 'Other'] }, { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Pending', 'Cancelled'] }, { name: 'cost', label: 'Cost/mo', type: 'number' }, { name: 'start_date', label: 'Start', type: 'date' }, { name: 'renewal_date', label: 'Renewal', type: 'date' }]} initialData={editingItem} onSubmit={(d) => saveItem('product', d)} saving={saving} />
      </Modal>
      <Modal isOpen={modalType === 'device'} onClose={() => setModalType(null)} title="Device">
        <GenericForm fields={[{ name: 'name', label: 'Name', required: true }, { name: 'type', label: 'Type', type: 'select', options: ['Laptop', 'Desktop', 'Server', 'Firewall', 'Switch', 'Access Point', 'Printer', 'Mobile', 'Other'] }, { name: 'os', label: 'OS' }, { name: 'serial', label: 'Serial' }, { name: 'location', label: 'Location' }, { name: 'status', label: 'Status', type: 'select', options: ['In Service', 'Retired', 'Repair'] }, { name: 'rustdesk_id', label: 'RustDesk ID' }, { name: 'rustdesk_password', label: 'RustDesk Password' }]} initialData={editingItem} onSubmit={(d) => saveItem('device', d)} saving={saving} />
      </Modal>
      <Modal isOpen={modalType === 'credential'} onClose={() => setModalType(null)} title="Login">
        <GenericForm fields={[{ name: 'system_name', label: 'System', required: true }, { name: 'url', label: 'URL' }, { name: 'username', label: 'Username' }, { name: 'password', label: 'Password' }, { name: 'mfa_details', label: 'MFA' }]} initialData={editingItem} onSubmit={(d) => saveItem('credential', d)} saving={saving} />
      </Modal>
      <Modal isOpen={modalType === 'billing'} onClose={() => setModalType(null)} title="Billing">
        <GenericForm fields={[{ name: 'month', label: 'Month (YYYY-MM)', required: true }, { name: 'description', label: 'Description' }, { name: 'quantity', label: 'Qty', type: 'number' }, { name: 'unit_price', label: 'Unit Price', type: 'number' }, { name: 'status', label: 'Status', type: 'select', options: ['Draft', 'Sent', 'Paid', 'Overdue'] }]} initialData={editingItem} onSubmit={(d) => saveItem('billing', d)} saving={saving} />
      </Modal>
    </div>
  );
};

interface FormField { name: string; label: string; type?: string; options?: string[]; required?: boolean; }

const GenericForm = ({ fields, initialData, onSubmit, saving }: { fields: FormField[]; initialData: any; onSubmit: (d: any) => void; saving: boolean }) => {
  const [formData, setFormData] = useState(initialData || {});
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setFormData({ ...formData, [e.target.name]: e.target.value });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      {fields.map((f) => (
        <div key={f.name}>
          {f.type === 'select' ? (
            <Select label={f.label} name={f.name} value={formData[f.name] || ''} onChange={handleChange} required={f.required}>
              <option value="">Select...</option>
              {f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
          ) : f.type === 'textarea' ? (
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">{f.label}</label><textarea className="w-full rounded-lg border p-3 text-sm" name={f.name} value={formData[f.name] || ''} onChange={handleChange} /></div>
          ) : (
            <Input label={f.label} name={f.name} type={f.type || 'text'} value={formData[f.name] || ''} onChange={handleChange} required={f.required} />
          )}
        </div>
      ))}
      <Button type="submit" disabled={saving} className="w-full py-4">{saving ? 'Saving...' : 'Save'}</Button>
    </form>
  );
};
